import { supabase } from './supabase'

// =====================================================================
// Shared helpers for the activity log (clock-icon feeds) and the
// notification system (persistent admin red-alerts).
//
// Design note: every write here is BEST-EFFORT. These are secondary,
// observability features — they must NEVER throw in a way that breaks
// the primary action (logging a session, approving, topping up, etc.).
// Each call is wrapped so failures only console.error.
// =====================================================================

export type ActivityScope = 'sessions' | 'students' | 'tutors' | 'topups' | 'global'

export type Actor = {
  id?: string | null
  email?: string | null
  role?: string | null
} | null | undefined

export type NotificationType =
  | 'session_approval'
  | 'rate_request'
  | 'consulting_followup'
  | 'topup'
  | 'low_balance'

export type NotificationSeverity = 'info' | 'warning' | 'alert'

/** Record an activity event. Surfaced in the per-page clock-icon feeds. */
export async function logActivity(params: {
  scope: ActivityScope
  action: string
  summary: string
  entityTable?: string
  entityId?: string | null
  metadata?: Record<string, any>
  actor?: Actor
}): Promise<void> {
  try {
    const { error } = await supabase.from('activity_log').insert({
      scope: params.scope,
      action: params.action,
      summary: params.summary,
      entity_table: params.entityTable ?? null,
      entity_id: params.entityId ?? null,
      metadata: params.metadata ?? null,
      actor_id: params.actor?.id ?? null,
      actor_email: params.actor?.email ?? null,
      actor_role: params.actor?.role ?? null,
    })
    if (error) throw error
  } catch (e) {
    console.error('logActivity failed (non-fatal):', e)
  }
}

/** Create a persistent notification (red-alert task). */
export async function createNotification(params: {
  type: NotificationType
  title: string
  message?: string
  relatedTable?: string
  relatedId?: string | null
  route?: string
  severity?: NotificationSeverity
  targetRole?: 'admin' | 'tutor'
}): Promise<void> {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert({
        type: params.type,
        title: params.title,
        message: params.message ?? null,
        related_table: params.relatedTable ?? null,
        related_id: params.relatedId ?? null,
        route: params.route ?? null,
        severity: params.severity ?? 'info',
        target_role: params.targetRole ?? 'admin',
      })
    if (error) throw error
  } catch (e) {
    console.error('createNotification failed (non-fatal):', e)
  }
}

/**
 * Create a notification only if an unresolved one of the same type doesn't
 * already exist for the same entity. Prevents duplicate alerts (e.g. repeated
 * low-balance warnings for the same student).
 */
export async function createNotificationOnce(params: {
  type: NotificationType
  title: string
  message?: string
  relatedTable?: string
  relatedId?: string | null
  route?: string
  severity?: NotificationSeverity
  targetRole?: 'admin' | 'tutor'
}): Promise<void> {
  try {
    if (params.relatedId) {
      const { data: existing } = await supabase
        .from('notifications')
        .select('id')
        .eq('type', params.type)
        .eq('related_id', params.relatedId)
        .eq('is_resolved', false)
        .limit(1)
      if (existing && existing.length > 0) return
    }
    await createNotification(params)
  } catch (e) {
    console.error('createNotificationOnce failed (non-fatal):', e)
  }
}

/** Balance (minutes) at/below which a student is flagged as low. */
export const LOW_BALANCE_THRESHOLD = 60

/**
 * Raise a low-balance alert when a student's balance drops to/below the
 * threshold. De-duplicated per student via createNotificationOnce. When the
 * balance recovers above the threshold, any existing alert is auto-cleared.
 */
export async function notifyLowBalance(
  studentId: string,
  balance: number,
  studentName: string
): Promise<void> {
  try {
    if (balance > LOW_BALANCE_THRESHOLD) {
      await resolveNotificationsFor('students', studentId, undefined, 'low_balance')
      return
    }
    await createNotificationOnce({
      type: 'low_balance',
      title: balance <= 0 ? `${studentName} is out of minutes` : `${studentName} is low on minutes`,
      message: `Balance is ${balance} min — consider a top-up.`,
      relatedTable: 'students',
      relatedId: studentId,
      route: `/dashboard/students/${studentId}`,
      severity: balance <= 0 ? 'alert' : 'warning',
    })
  } catch (e) {
    console.error('notifyLowBalance failed (non-fatal):', e)
  }
}

/** Mark a single notification resolved ("Done"). */
export async function resolveNotification(id: string, resolvedBy?: string | null): Promise<void> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({
        is_resolved: true,
        resolved_by: resolvedBy ?? null,
        resolved_at: new Date().toISOString(),
      })
      .eq('id', id)
    if (error) throw error
  } catch (e) {
    console.error('resolveNotification failed (non-fatal):', e)
  }
}

/**
 * Auto-resolve any unresolved notifications attached to a specific entity.
 * Used so that, e.g., approving a session from the table also clears its
 * pending-approval alert without the admin clicking "Done" twice.
 */
export async function resolveNotificationsFor(
  relatedTable: string,
  relatedId: string,
  resolvedBy?: string | null,
  type?: NotificationType
): Promise<void> {
  try {
    let q = supabase
      .from('notifications')
      .update({
        is_resolved: true,
        resolved_by: resolvedBy ?? null,
        resolved_at: new Date().toISOString(),
      })
      .eq('related_table', relatedTable)
      .eq('related_id', relatedId)
      .eq('is_resolved', false)
    if (type) q = q.eq('type', type)
    const { error } = await q
    if (error) throw error
  } catch (e) {
    console.error('resolveNotificationsFor failed (non-fatal):', e)
  }
}
